import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import ImageExtension from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { useState, useCallback, useEffect, useRef } from 'react';
import type { JSONContent } from '@tiptap/react';
import { Callout } from './CalloutExtension';
import { SlashCommandMenu } from './SlashCommandMenu';

const lowlight = createLowlight(common);

interface SteleEditorProps {
  content?: JSONContent;
  onUpdate?: (json: JSONContent) => void;
  editable?: boolean;
  placeholder?: string;
}

export function SteleEditor({
  content,
  onUpdate,
  editable = true,
  placeholder = 'Type / for commands...',
}: SteleEditorProps) {
  const [slashMenu, setSlashMenu] = useState<{
    isOpen: boolean;
    position: { top: number; left: number };
    query: string;
  }>({
    isOpen: false,
    position: { top: 0, left: 0 },
    query: '',
  });

  const editorContainerRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // we use CodeBlockLowlight instead
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
        showOnlyCurrent: true,
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      ImageExtension.configure({
        inline: false,
        allowBase64: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline underline-offset-2',
        },
      }),
      CodeBlockLowlight.configure({
        lowlight,
        defaultLanguage: null,
      }),
      Callout,
    ],
    content: content ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    editable,
    onUpdate: ({ editor: ed }) => {
      onUpdate?.(ed.getJSON());
    },
    editorProps: {
      attributes: {
        class: 'stele-editor prose prose-neutral dark:prose-invert max-w-none focus:outline-none min-h-[300px] px-0 py-2',
      },
      handleKeyDown: (_view, event) => {
        // Slash command triggering is handled via onUpdate
        if (event.key === 'Escape' && slashMenu.isOpen) {
          setSlashMenu(prev => ({ ...prev, isOpen: false }));
          return true;
        }
        return false;
      },
    },
  });

  // Handle slash command detection
  const handleSlashDetection = useCallback(() => {
    if (!editor) return;

    const { state } = editor;
    const { from } = state.selection;
    const textBefore = state.doc.textBetween(
      Math.max(0, from - 50),
      from,
      '\n'
    );

    const slashIndex = textBefore.lastIndexOf('/');
    if (slashIndex >= 0) {
      const afterSlash = textBefore.slice(slashIndex + 1);
      // Only open if slash is at start of line or after space
      const charBefore = slashIndex > 0 ? textBefore[slashIndex - 1] : '\n';
      if (charBefore === '\n' || charBefore === ' ' || slashIndex === 0) {
        // No spaces in the query after the slash
        if (!afterSlash.includes(' ') && afterSlash.length <= 20) {
          // Get cursor position for menu
          const coords = editor.view.coordsAtPos(from);
          setSlashMenu({
            isOpen: true,
            position: {
              top: coords.bottom + 4,
              left: coords.left,
            },
            query: afterSlash,
          });
          return;
        }
      }
    }

    if (slashMenu.isOpen) {
      setSlashMenu(prev => ({ ...prev, isOpen: false }));
    }
  }, [editor, slashMenu.isOpen]);

  useEffect(() => {
    if (!editor) return;
    editor.on('update', handleSlashDetection);
    return () => {
      editor.off('update', handleSlashDetection);
    };
  }, [editor, handleSlashDetection]);

  // Update content when prop changes
  useEffect(() => {
    if (editor && content && !editor.isDestroyed) {
      const currentJSON = JSON.stringify(editor.getJSON());
      const newJSON = JSON.stringify(content);
      if (currentJSON !== newJSON) {
        editor.commands.setContent(content);
      }
    }
  }, [editor, content]);

  if (!editor) return null;

  return (
    <div ref={editorContainerRef} className="relative">
      <EditorContent editor={editor} />
      <SlashCommandMenu
        editor={editor}
        isOpen={slashMenu.isOpen}
        onClose={() => setSlashMenu(prev => ({ ...prev, isOpen: false }))}
        position={slashMenu.position}
        query={slashMenu.query}
      />
    </div>
  );
}
